import { ref } from 'vue'
import type { Ref } from 'vue'

export type StageDiffResetScope = 'current' | 'all'

export interface StageDiffResetDependencies {
  readonly reset: (scope: StageDiffResetScope) => Promise<boolean>
  readonly currentError: () => string | null
  readonly fallbackError: () => string
}

export interface StageDiffResetController {
  readonly confirmationOpen: Ref<boolean>
  readonly resetPending: Ref<boolean>
  readonly resetError: Ref<string | null>
  readonly requestReset: (allowed: boolean) => void
  readonly updateConfirmation: (open: boolean) => void
  readonly closeConfirmation: () => void
  readonly confirm: (scope: StageDiffResetScope) => Promise<boolean>
}

export function useStageDiffReset(
  dependencies: StageDiffResetDependencies,
): StageDiffResetController {
  const confirmationOpen = ref<boolean>(false)
  const resetPending = ref<boolean>(false)
  const resetError = ref<string | null>(null)

  function requestReset(allowed: boolean): void {
    if (!allowed || resetPending.value) return
    resetError.value = null
    confirmationOpen.value = true
  }

  function updateConfirmation(open: boolean): void {
    if (!resetPending.value) confirmationOpen.value = open
  }

  function closeConfirmation(): void {
    if (!resetPending.value) confirmationOpen.value = false
  }

  async function confirm(scope: StageDiffResetScope): Promise<boolean> {
    if (resetPending.value) return false
    resetPending.value = true
    resetError.value = null
    let succeeded = false
    try {
      succeeded = await dependencies.reset(scope)
    } catch (error: unknown) {
      resetError.value = error instanceof Error ? error.message : String(error)
    }
    resetPending.value = false
    if (!succeeded) {
      resetError.value ??= dependencies.currentError() ?? dependencies.fallbackError()
      return false
    }
    confirmationOpen.value = false
    return true
  }

  return {
    confirmationOpen,
    resetPending,
    resetError,
    requestReset,
    updateConfirmation,
    closeConfirmation,
    confirm,
  }
}
